import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const WorkspaceContext = createContext(null);

// Re-poll the workspace list this often. Workspaces don't change frequently,
// but a tab open for hours should still pick up an invite / rename / leave
// done in another session.
const POLL_MS = 30000;

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!user || !user.id) return null;
    try {
      const { data } = await api.get("/workspaces");
      const list = Array.isArray(data) ? data : [];
      setWorkspaces(list);
      setError(null);
      setLoading(false);
      return list;
    } catch (e) {
      setError(formatApiError(e));
      setLoading(false);
      return null;
    }
  }, [user]);

  useEffect(() => {
    if (user && user.id) {
      setLoading(true);
      refetch();
    } else {
      setWorkspaces([]);
      setLoading(false);
    }
  }, [user, refetch]);

  useEffect(() => {
    if (!user || !user.id) return undefined;
    const id = setInterval(refetch, POLL_MS);
    return () => clearInterval(id);
  }, [user, refetch]);

  return (
    <WorkspaceContext.Provider
      value={{ workspaces, loading, error, refetch, setWorkspaces }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspaces() {
  // For public routes (/, /signup, /join/:token) we render WITHOUT a
  // WorkspaceProvider in the tree. Return null instead of throwing so
  // consumers can guard with `const ws = useWorkspaces();` without crashing.
  return useContext(WorkspaceContext);
}
